import type {
  DepartmentHealthActionItem,
  DepartmentHealthActionPriority,
  DepartmentHealthActionType,
  DepartmentHealthBenchmarks,
  DepartmentHealthConfidence,
  DepartmentHealthDataFreshness,
  DepartmentHealthDetail,
  DepartmentHealthDimensionScores,
  DepartmentHealthNarrative,
  DepartmentHealthEffortLevel,
  DepartmentHealthProfileDecisionGuide,
  DepartmentHealthProfilePath,
  DepartmentHealthProfileSummary,
  DepartmentHealthRoleCoverageItem,
  DepartmentHealthScores,
  DepartmentHealthSkillCoverage,
  DepartmentHealthSkillCoverageItem,
  DepartmentHealthState,
} from '../types/DepartmentHealthTypes';

import {toPercentScore} from './WorkforceProfiles/utils';
import {ApiSerializer} from '../../../../serializers/ApiSerializer';
import {RequiredLanguageProfile} from '../../../../types/shared-types';

interface ApiDimensionScores {
  overall?: number | null;
  coreCompetencies?: number | null;
  hardSkills?: number | null;
  tools?: number | null;
  domainKnowledge?: number | null;
  softSkills?: number | null;
  responsibilities?: number | null;
  // Legacy v1 keys, tolerated while the API soaks the v2 rollout.
  competencies?: number | null;
  knowledge?: number | null;
}

interface ApiDepartmentHealthPayload {
  departmentId?: string;
  scores?: {
    profileCount?: number;
    departmentAverage?: ApiDimensionScores | null;
    profilesBelowLimit?: number;
    profilesAtOrAboveLimit?: number;
    lowestProfileScore?: number | null;
    highestProfileScore?: number | null;
  };
  benchmarks?: {
    mixedRoles?: boolean;
    roleNorm?: ApiDimensionScores | null;
    companyAverage?: ApiDimensionScores;
    deltas?: ApiDimensionScores | null;
  };
  narrative?: {
    healthState?: string;
  };
  dataFreshness?: {
    freshCount?: number;
    ageingCount?: number;
    staleCount?: number;
    veryStaleCount?: number;
    freshPct?: number;
    ageingPct?: number;
    staleBandPct?: number;
    veryStalePct?: number;
    outdatedPct?: number;
    confidence?: string;
  };
  profileDecisionGuide?: {
    mostUrgent?: ApiProfileSummary[];
    redeployCandidates?: ApiProfileSummary[];
    developCandidates?: ApiProfileSummary[];
    onTrack?: ApiProfileSummary[];
  };
  actionItems?: ApiActionItem[];
  skillCoverage?: {
    critical?: ApiSkillCoverageItem[];
    partial?: ApiSkillCoverageItem[];
    full?: ApiSkillCoverageItem[];
  };
  roleCoverage?: ApiRoleCoverageItem[];
}

interface ApiSkillCoverageItem {
  skillName?: ApiBilingualText;
  coveragePct?: number;
}

interface ApiRoleCoverageItem {
  roleTitle?: string;
  targetProfileId?: string;
  profileCount?: number;
  averageAlignmentScore?: number | null;
  unmetCompetencies?: ApiBilingualText[];
}

interface ApiBilingualText {
  en?: string;
  de?: string;
}

interface ApiActionItem {
  actionType?: string;
  priority?: string | null;
  recommendation?: ApiBilingualText;
  expectedOutcome?: ApiBilingualText;
  profilePublicIds?: string[];
  topCompetencies?: ApiBilingualText[];
  roleTitle?: string | null;
  affectedProfileCount?: number;
}

interface ApiProfileSummary {
  profileId?: string;
  publicId?: string;
  targetProfileAlignmentScore?: number | null;
  gapCount?: number;
  path?: string | null;
  effortLevel?: string | null;
}

const mapDimensionScores = (raw: ApiDimensionScores | null | undefined): DepartmentHealthDimensionScores | null => {
  if (!raw) {
    return null;
  }

  return {
    overall: raw.overall ?? null,
    coreCompetencies: raw.coreCompetencies ?? raw.competencies ?? null,
    hardSkills: raw.hardSkills ?? raw.knowledge ?? null,
    tools: raw.tools ?? null,
    domainKnowledge: raw.domainKnowledge ?? null,
    softSkills: raw.softSkills ?? null,
    responsibilities: raw.responsibilities ?? null,
  };
};

const mapScores = (raw: ApiDepartmentHealthPayload['scores']): DepartmentHealthScores => ({
  profileCount: raw?.profileCount ?? 0,
  departmentAverage: mapDimensionScores(raw?.departmentAverage),
  profilesBelowLimit: raw?.profilesBelowLimit ?? 0,
  profilesAtOrAboveLimit: raw?.profilesAtOrAboveLimit ?? 0,
  lowestProfileScore: raw?.lowestProfileScore ?? null,
  highestProfileScore: raw?.highestProfileScore ?? null,
});

const mapBenchmarks = (raw: ApiDepartmentHealthPayload['benchmarks']): DepartmentHealthBenchmarks => ({
  mixedRoles: raw?.mixedRoles ?? false,
  roleNorm: mapDimensionScores(raw?.roleNorm),
  companyAverage: mapDimensionScores(raw?.companyAverage) ?? {
    overall: null,
    coreCompetencies: null,
    hardSkills: null,
    tools: null,
    domainKnowledge: null,
    softSkills: null,
    responsibilities: null,
  },
  deltas: mapDimensionScores(raw?.deltas),
});

const mapHealthState = (raw: string | undefined): DepartmentHealthState => {
  if (raw === 'well_aligned' || raw === 'functional' || raw === 'at_risk') {
    return raw;
  }

  return 'at_risk';
};

const mapConfidence = (raw: string | undefined): DepartmentHealthConfidence => {
  if (raw === 'high' || raw === 'medium' || raw === 'low') {
    return raw;
  }

  return 'low';
};

const mapNarrative = (raw: ApiDepartmentHealthPayload['narrative']): DepartmentHealthNarrative => ({
  healthState: mapHealthState(raw?.healthState),
});

const mapDataFreshness = (raw: ApiDepartmentHealthPayload['dataFreshness']): DepartmentHealthDataFreshness => ({
  freshCount: raw?.freshCount ?? 0,
  ageingCount: raw?.ageingCount ?? 0,
  staleCount: raw?.staleCount ?? 0,
  veryStaleCount: raw?.veryStaleCount ?? 0,
  freshPct: raw?.freshPct ?? 0,
  ageingPct: raw?.ageingPct ?? 0,
  staleBandPct: raw?.staleBandPct ?? 0,
  veryStalePct: raw?.veryStalePct ?? 0,
  outdatedPct: raw?.outdatedPct ?? 0,
  confidence: mapConfidence(raw?.confidence),
});

const mapProfilePath = (raw: string | null | undefined): DepartmentHealthProfilePath | null => {
  if (raw === 'develop' || raw === 'redeploy') {
    return raw;
  }

  return null;
};

const mapEffortLevel = (raw: string | null | undefined): DepartmentHealthEffortLevel | null => {
  if (raw === 'low' || raw === 'moderate' || raw === 'high') {
    return raw;
  }

  return null;
};

const mapProfileSummary = (raw: ApiProfileSummary): DepartmentHealthProfileSummary => ({
  profileId: raw.profileId ?? '',
  publicId: raw.publicId ?? '',
  targetProfileAlignmentScore: toPercentScore(raw.targetProfileAlignmentScore),
  gapCount: raw.gapCount ?? 0,
  path: mapProfilePath(raw.path),
  effortLevel: mapEffortLevel(raw.effortLevel),
});

const mapProfileDecisionGuide = (
  raw: ApiDepartmentHealthPayload['profileDecisionGuide']
): DepartmentHealthProfileDecisionGuide => ({
  mostUrgent: (raw?.mostUrgent ?? []).map(mapProfileSummary),
  redeployCandidates: (raw?.redeployCandidates ?? []).map(mapProfileSummary),
  developCandidates: (raw?.developCandidates ?? []).map(mapProfileSummary),
  onTrack: (raw?.onTrack ?? []).map(mapProfileSummary),
});

const mapSkillCoverageItem = (raw: ApiSkillCoverageItem): DepartmentHealthSkillCoverageItem => ({
  skillName: mapBilingualText(raw.skillName),
  coveragePct: raw.coveragePct ?? 0,
});

const mapSkillCoverage = (raw: ApiDepartmentHealthPayload['skillCoverage']): DepartmentHealthSkillCoverage => ({
  critical: (raw?.critical ?? []).map(mapSkillCoverageItem),
  partial: (raw?.partial ?? []).map(mapSkillCoverageItem),
  full: (raw?.full ?? []).map(mapSkillCoverageItem),
});

const mapRoleCoverageItem = (raw: ApiRoleCoverageItem): DepartmentHealthRoleCoverageItem => ({
  roleTitle: raw.roleTitle ?? '',
  targetProfileId: raw.targetProfileId ?? '',
  profileCount: raw.profileCount ?? 0,
  averageAlignmentScore: toPercentScore(raw.averageAlignmentScore),
  unmetCompetencies: (raw.unmetCompetencies ?? []).map(mapBilingualText),
});

const mapBilingualText = (raw: ApiBilingualText | null | undefined): RequiredLanguageProfile => ({
  en: raw?.en ?? '',
  de: raw?.de ?? '',
});

const mapActionType = (raw: string | undefined): DepartmentHealthActionType => {
  const valid: DepartmentHealthActionType[] = [
    'skill_training',
    'single_point_of_failure',
    'redeployment_review',
    'development_plans',
    'data_quality',
    'all_clear',
  ];

  if (valid.includes(raw as DepartmentHealthActionType)) {
    return raw as DepartmentHealthActionType;
  }

  return 'all_clear';
};

const mapActionPriority = (raw: string | null | undefined): DepartmentHealthActionPriority | null => {
  if (raw === 'high' || raw === 'medium' || raw === 'low') {
    return raw;
  }

  return null;
};

const mapActionItem = (raw: ApiActionItem): DepartmentHealthActionItem => ({
  actionType: mapActionType(raw.actionType),
  priority: mapActionPriority(raw.priority),
  recommendation: mapBilingualText(raw.recommendation),
  expectedOutcome: mapBilingualText(raw.expectedOutcome),
  profilePublicIds: raw.profilePublicIds ?? [],
  topCompetencies: (raw.topCompetencies ?? []).map(mapBilingualText),
  roleTitle: raw.roleTitle ?? null,
  affectedProfileCount: raw.affectedProfileCount ?? 0,
});

export const mapDepartmentHealthResponse = (raw: unknown): DepartmentHealthDetail => {
  const body = ApiSerializer.toCamelCase(raw) as {departmentHealth?: ApiDepartmentHealthPayload};
  const health = body.departmentHealth ?? (ApiSerializer.toCamelCase(raw) as ApiDepartmentHealthPayload);

  return {
    departmentId: health.departmentId ?? '',
    scores: mapScores(health.scores),
    benchmarks: mapBenchmarks(health.benchmarks),
    narrative: mapNarrative(health.narrative),
    actionItems: (health.actionItems ?? []).map(mapActionItem),
    dataFreshness: mapDataFreshness(health.dataFreshness),
    profileDecisionGuide: mapProfileDecisionGuide(health.profileDecisionGuide),
    skillCoverage: mapSkillCoverage(health.skillCoverage),
    roleCoverage: (health.roleCoverage ?? []).map(mapRoleCoverageItem),
  };
};
